import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import Select from "react-select";
import { Box, InputLabel } from "@mui/material";
import TextInputComponent from "../text-input-component/text-input-component";
import {
  addressData,
  extractAllOptions,
  filterOptionsByValue,
} from "../../../utils/extractStateCityZipCode";
import {
  cityOptions,
  stateOptions,
  codeOptions,
} from "../../../utils/extractStateCityZipCode";
import "./address-from-fields.scss";
import { debounce } from "lodash";

type Props = {
  namePrefix?: string;
  postCode?: string;
  isOutlineInputs?: boolean;
  disabled?: boolean;
};

const countryOptions = [{ label: "United States", value: "United States" }];

const AddressFormFields: React.FC<Props> = ({
  namePrefix,
  postCode = "postcode",
  isOutlineInputs = false,
  disabled = false,
}) => {
  const { values, setFieldValue, handleBlur, handleChange, errors } =
    useFormikContext<any>();
  const getField = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : `${field}`;
  const currentValues = namePrefix ? values?.[namePrefix] : values || {};
  const [zipCodePage, setZipCodePage] = useState(1);
  const PAGE_SIZE = 100;

  const [filteredOptions, setFilteredOptions] = useState<any>({
    cityOptions: cityOptions || [],
    stateOptions: stateOptions || [],
    postCodeOptions: codeOptions || [],
    filtered: [],
  });

  const [inputValue, setInputValue] = useState<any>({});

  const handleInputValue = useMemo(
    () =>
      debounce((value: string, field: string) => {
        setInputValue({ value, field });
      }, 500),
    []
  );

  const handleCityChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue(getField("city"), "");
      return;
    }
    setFieldValue(getField("city"), option?.value);

    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "City",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "city");
    setFilteredOptions({ ...result });
  };

  const findValueorNot = (options: any, value: string) => {
    return options?.find((opt: any) => opt.value === value) || null;
  };

  const handleToCheckValueExitsInOptions = (options: any, key: string) => {
    if (
      currentValues?.state &&
      key !== "state" &&
      !findValueorNot(options?.stateOptions, currentValues?.state)
    ) {
      setFieldValue(getField("state"), "");
    }
    if (
      currentValues?.city &&
      key !== "city" &&
      !findValueorNot(options?.cityOptions, currentValues?.city)
    ) {
      setFieldValue(getField("city"), "");
    }
    if (
      currentValues?.[postCode] &&
      key !== "code" &&
      !findValueorNot(options?.postCodeOptions, currentValues?.[postCode])
    ) {
      setFieldValue(getField(postCode), "");
    }
  };

  const handlePincodeChange = (option: any) => {
    if (!option) {
      // User cleared the input
      setFieldValue(getField(postCode), "");
      setFilteredOptions((prev: any) => ({
        ...prev,
        postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
      }));
      setZipCodePage(1);
      return;
    }
    setFieldValue(getField(postCode), option?.value);
    const result = filterOptionsByValue(
      filteredOptions?.filtered,
      "Code",
      option?.value
    );
    handleToCheckValueExitsInOptions(result, "code");
    setFilteredOptions({ ...result });
  };

  const displayedZipCodes = useMemo(() => {
    setFilteredOptions((prev: any) => ({
      ...prev,
      postCodeOptions: codeOptions?.slice(0, zipCodePage * PAGE_SIZE),
    }));
    return codeOptions?.slice(0, zipCodePage * PAGE_SIZE);
  }, [zipCodePage]);

  useEffect(() => {
    if (inputValue?.value?.length >= 2) {
      const lower = inputValue?.value?.toLowerCase();
      const key = inputValue?.field;
      const data = key === "City" ? filteredOptions?.filtered : addressData;
      if (data?.length) {
        const filtered = data?.filter((c: any) =>
          c?.[key]?.toLowerCase().includes(lower)
        );
        const optionsData = extractAllOptions(filtered);
        if (key === "City") {
          setFilteredOptions((prev: any) => ({
            ...prev,
            cityOptions: optionsData?.cityOptions?.slice(0, 250),
            filtered,
          }));
        } else {
          const options: any = {};
          if (key === "State") {
            options.stateOptions = optionsData?.stateOptions?.slice(0, 100);
          }
          if (key === "Code") {
            options.postCodeOptions = optionsData?.postCodeOptions?.slice(
              0,
              250
            );
          }
          setFilteredOptions((prev: any) => ({
            ...prev,
            ...options,
            filtered,
          }));
        }
      }
    } else {
      setFilteredOptions({
        cityOptions: cityOptions || [],
        stateOptions: stateOptions || [],
        postCodeOptions: displayedZipCodes || [],
        filtered: [],
      });
    }
  }, [inputValue]);

  useEffect(() => {
    let result;

    if (currentValues?.[postCode]) {
      result = filterOptionsByValue(
        addressData,
        "Code",
        currentValues[postCode]
      );
    } else if (currentValues?.city) {
      result = filterOptionsByValue(
        addressData,
        "City",
        currentValues.city,
        "State",
        currentValues.state
      );
    } else if (currentValues?.state) {
      result = filterOptionsByValue(addressData, "State", currentValues.state);
    }
    if (result) {
      const noFilteredData = !result?.filtered?.length && !inputValue?.value;

      setFilteredOptions(
        noFilteredData
          ? {
              cityOptions: cityOptions || [],
              stateOptions: stateOptions || [],
              postCodeOptions: codeOptions || [],
              filtered: [],
            }
          : { ...result }
      );
    }
  }, [currentValues?.[postCode], currentValues?.city, currentValues?.state]);
  const getSelectedOption = (options: any[] = [], value?: string) => {
    if (!value) return null;
 
    const matchedOption = options.find((opt: any) => opt.value === value);
    if (matchedOption) return matchedOption;
 
    // fallback only for display purpose
    return { label: value, value };
  };
  return (
    <>
      {isOutlineInputs ? (
        <Box
          className="address-details-content address-details-wrapper "
          gap={3}
        >
          <Box className="address-content-header">
            <ul className="equal-width-list">
              <li className="list-item">
                <span className="item-label">Street Line 1</span>
                <span className="item-value">
                  <TextInputComponent
                    fieldName={getField("streetLine1")}
                    // label="Street Line 1"
                    placeholder="Enter street line 1"
                    value={currentValues?.streetLine1 || ""}
                    onChange={handleChange}
                    handleBlur={handleBlur}
                    type="text"
                    maxlength="50"
                    isStreet
                    disabled={disabled}
                    autoComplete="new-password"
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Street Line 2</span>
                <span className="item-value">
                  <TextInputComponent
                    fieldName={getField("streetLine2")}
                    // label="Street Line 2"
                    placeholder="Enter street line 2"
                    value={currentValues?.streetLine2 || ""}
                    onChange={handleChange}
                    handleBlur={handleBlur}
                    type="text"
                    maxlength="50"
                    isStreet
                    disabled={disabled}
                    autoComplete="new-password"
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">State</span>
                <span className="item-value">
                  <Select
                    inputId="State"
                    options={filteredOptions.stateOptions?.sort(
                      (a: any, b: any) => a?.value.localeCompare(b?.value)
                    )}
                    placeholder="Select state"
                    classNamePrefix="react-select"
                    value={
                      filteredOptions?.stateOptions?.find(
                        (opt: any) => opt.value === currentValues?.state
                      ) || null
                    }
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    // onInputChange={(val) => handleInputValue(val, "State")}
                    onInputChange={(val, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        handleInputValue(val, "State");
                      }
                    }}
                    onChange={(option: any) => {
                      if (!option) {
                        // User cleared the input
                        setFilteredOptions((prev: any) => ({
                          cityOptions: [],
                          postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
                          filtered: [],
                          stateOptions: stateOptions, // reset full list
                        }));
                        setFieldValue(getField("state"), "");
                        setFieldValue(getField("city"), "");
                        setFieldValue(getField(postCode), "");
                        setZipCodePage(1);
                        return;
                      }
                      const result = filterOptionsByValue(
                        filteredOptions?.filtered,
                        "State",
                        option?.value
                      );
                      handleToCheckValueExitsInOptions(result, "state");
                      setFilteredOptions({ ...result });
                      setFieldValue(getField("state"), option?.value);
                    }}
                    isClearable={true}
                    isSearchable={true}
                    isDisabled={disabled}
                  />
                </span>
              </li>
            </ul>
          </Box>
          <Box className="address-content-header">
            <ul className="equal-width-list">
              <li className="list-item">
                <span className="item-label">Country</span>
                <span className="item-value">
                  <Select
                    inputId="Country"
                    classNamePrefix="react-select"
                    options={countryOptions}
                    placeholder="Select country"
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    value={
                      countryOptions.find(
                        (opt: any) => opt.value === currentValues?.country
                      ) || null
                    }
                    onChange={(option: any) =>
                      setFieldValue(getField("country"), option?.value)
                    }
                    isClearable={false}
                    isDisabled={disabled}
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">City</span>
                <span className="item-value">
                  <Select
                    inputId="City"
                    classNamePrefix="react-select"
                    options={filteredOptions.cityOptions?.sort(
                      (a: any, b: any) => a?.value.localeCompare(b?.value)
                    )}
                    placeholder="Select city"
                    isDisabled={
                      (!currentValues?.state && !currentValues?.[postCode]) ||
                      disabled
                    }
                    value={getSelectedOption(filteredOptions.cityOptions, currentValues?.city)}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    onChange={handleCityChange}
                    isClearable={true}
                    isSearchable={true}
                  />
                </span>
              </li>
              <li className="list-item">
                <span className="item-label">Zip Code</span>
                <span className="item-value">
                  <Select
                    inputId="Postcode"
                    classNamePrefix="react-select"
                    options={filteredOptions.postCodeOptions}
                    placeholder="Select zip code"
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    value={getSelectedOption(filteredOptions.postCodeOptions, currentValues?.[postCode])}
                    // onInputChange={(val) => handleInputValue(val, "Code")}
                    onInputChange={(val, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        handleInputValue(val, "Code");
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      //if there is no state and city selected, load more zip codes
                      //if there is state or city selected then based on that value we will get zip codes
                      if (
                        !currentValues?.state &&
                        !currentValues?.city &&
                        inputValue.field !== "Code" &&
                        zipCodePage * PAGE_SIZE < codeOptions?.length
                      ) {
                        setZipCodePage((prev) => prev + 1);
                      }
                    }}
                    onChange={handlePincodeChange}
                    isClearable={true}
                    isSearchable={true}
                    isDisabled={disabled}
                  />
                </span>
              </li>
            </ul>
          </Box>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={2}
          className="address-form-fields"
        >
          <TextInputComponent
            fieldName={getField("streetLine1")}
            label="Street Line 1"
            placeholder="Enter street line 1"
            value={currentValues?.streetLine1 || ""}
            onChange={handleChange}
            type="text"
            maxlength="50"
            isStreet
            disabled={disabled}
            autoComplete="new-password"
          />

          <TextInputComponent
            fieldName={getField("streetLine2")}
            label="Street Line 2"
            placeholder="Enter street line 2"
            value={currentValues?.streetLine2 || ""}
            onChange={handleChange}
            type="text"
            maxlength="50"
            isStreet
            disabled={disabled}
            autoComplete="new-password"
          />

          {/* State */}
          <div className="form-group">
            <InputLabel htmlFor="State">State</InputLabel>
            <Select
              inputId="State"
              options={filteredOptions.stateOptions?.sort((a: any, b: any) =>
                a?.value.localeCompare(b?.value)
              )}
              placeholder="Select state"
              classNamePrefix="react-select"
              required={false}
              value={
                filteredOptions?.stateOptions?.find(
                  (opt: any) => opt.value === currentValues?.state
                ) || null
              }
              components={{
                IndicatorSeparator: () => null,
              }}
              // onInputChange={(val) => handleInputValue(val, "State")}
              onInputChange={(val, actionMeta) => {
                if (actionMeta.action === "input-change") {
                  handleInputValue(val, "State");
                }
              }}
              onChange={(option: any) => {
                if (!option) {
                  // User cleared the input
                  setFilteredOptions((prev: any) => ({
                    cityOptions: [],
                    postCodeOptions: codeOptions?.slice(0, 1 * PAGE_SIZE),
                    filtered: [],
                    stateOptions: stateOptions, // reset full list
                  }));
                  setFieldValue(getField("state"), "");
                  setFieldValue(getField("city"), "");
                  setFieldValue(getField(postCode), "");
                  setZipCodePage(1);
                  return;
                }
                const result = filterOptionsByValue(
                  filteredOptions?.filtered,
                  "State",
                  option?.value
                );
                handleToCheckValueExitsInOptions(result, "state");
                setFilteredOptions({ ...result });
                setFieldValue(getField("state"), option?.value);
              }}
              isClearable={true}
              isSearchable={true}
              isDisabled={disabled}
            />
          </div>

          {/* City */}
          <div className="form-group">
            <InputLabel htmlFor="City">City</InputLabel>
            <Select
              inputId="City"
              classNamePrefix="react-select"
              options={filteredOptions.cityOptions?.sort((a: any, b: any) =>
                a?.value.localeCompare(b?.value)
              )}
              placeholder="Select city"
              isDisabled={
                (!currentValues?.state && !currentValues?.[postCode]) ||
                disabled
              }
              value={
                filteredOptions.cityOptions.find(
                  (opt: any) => opt.value === currentValues?.city
                ) || null
              }
              components={{
                IndicatorSeparator: () => null,
              }}
              onChange={handleCityChange}
              isClearable={true}
              isSearchable={true}
            />
          </div>

          {/* Country (static options, if needed) */}
          <div className="form-group">
            <InputLabel htmlFor="Country">Country</InputLabel>
            <Select
              inputId="Country"
              classNamePrefix="react-select"
              options={countryOptions}
              placeholder="Select country"
              components={{
                IndicatorSeparator: () => null,
              }}
              value={
                countryOptions.find(
                  (opt: any) => opt.value === currentValues?.country
                ) || null
              }
              onChange={(option: any) =>
                setFieldValue(getField("country"), option?.value)
              }
              isClearable={false}
              isDisabled={disabled}
            />
          </div>

          {/* Postcode */}
          <div className="form-group">
            <InputLabel htmlFor="Postcode">Zip Code</InputLabel>
            <Select
              inputId="Postcode"
              classNamePrefix="react-select"
              options={filteredOptions.postCodeOptions}
              placeholder="Select zip code"
              components={{
                IndicatorSeparator: () => null,
              }}
              value={
                filteredOptions.postCodeOptions.find(
                  (opt: any) => opt.value === currentValues?.[postCode]
                ) || null
              }
              // onInputChange={(val) => handleInputValue(val, "Code")}
              onInputChange={(val, actionMeta) => {
                if (actionMeta.action === "input-change") {
                  handleInputValue(val, "Code");
                }
              }}
              onMenuScrollToBottom={() => {
                //if there is no state and city selected, load more zip codes
                //if there is state or city selected then based on that value we will get zip codes
                if (
                  !currentValues?.state &&
                  !currentValues?.city &&
                  inputValue.field !== "Code" &&
                  zipCodePage * PAGE_SIZE < codeOptions?.length
                ) {
                  setZipCodePage((prev) => prev + 1);
                }
              }}
              onChange={handlePincodeChange}
              isClearable={true}
              isSearchable={true}
              isDisabled={disabled}
            />
          </div>
        </Box>
      )}
    </>
  );
};

export default AddressFormFields;
